import { useRef } from "react";
import { useTranslation } from "react-i18next";
import { cn } from "~/foundation/utils/cn";
import { SIDEBAR_MAX_WIDTH, SIDEBAR_MIN_WIDTH, useShellStore } from "~/shell/store";

const KEYBOARD_STEP = 16;

/**
 * Drag handle on the sidebar's inline-end edge, following the WAI-ARIA window-splitter
 * pattern (role="separator", arrow keys, Home/End). Pointer-driven with no dependency:
 * pointer capture keeps the drag alive when the cursor outruns the 6px strip.
 *
 * RTL-aware by measurement, not by convention: the width is always the distance from the
 * sidebar's *start* edge to the pointer, so dragging outward grows the sidebar under both
 * directions, and the keyboard arrows follow the visual direction of growth.
 */
export function SidebarResizeHandle() {
  const { t } = useTranslation();
  const sidebarWidth = useShellStore((state) => state.sidebarWidth);
  const setSidebarWidth = useShellStore((state) => state.setSidebarWidth);
  const resetSidebarWidth = useShellStore((state) => state.resetSidebarWidth);
  const setSidebarResizing = useShellStore((state) => state.setSidebarResizing);

  const drag = useRef<{ start: number; rtl: boolean } | null>(null);

  const measure = (element: HTMLElement): { start: number; rtl: boolean } | null => {
    const aside = element.closest("aside");
    if (!aside) {
      return null;
    }
    const rect = aside.getBoundingClientRect();
    const rtl = getComputedStyle(aside).direction === "rtl";
    return { start: rtl ? rect.right : rect.left, rtl };
  };

  return (
    <div
      role="separator"
      aria-orientation="vertical"
      aria-label={t("Resize sidebar")}
      aria-valuemin={SIDEBAR_MIN_WIDTH}
      aria-valuemax={SIDEBAR_MAX_WIDTH}
      aria-valuenow={sidebarWidth}
      tabIndex={0}
      className={cn(
        "absolute inset-y-0 end-0 z-10 w-1.5 cursor-col-resize touch-none select-none",
        "transition-colors hover:bg-divider focus-visible:bg-accent/40 focus-visible:outline-none",
      )}
      onPointerDown={(event) => {
        if (event.button !== 0) {
          return;
        }
        const measured = measure(event.currentTarget);
        if (!measured) {
          return;
        }
        drag.current = measured;
        event.currentTarget.setPointerCapture(event.pointerId);
        setSidebarResizing(true);
      }}
      onPointerMove={(event) => {
        if (!drag.current) {
          return;
        }
        const { start, rtl } = drag.current;
        setSidebarWidth(rtl ? start - event.clientX : event.clientX - start);
      }}
      onPointerUp={(event) => {
        if (drag.current) {
          drag.current = null;
          event.currentTarget.releasePointerCapture(event.pointerId);
          setSidebarResizing(false);
        }
      }}
      onPointerCancel={(event) => {
        if (drag.current) {
          drag.current = null;
          event.currentTarget.releasePointerCapture(event.pointerId);
          setSidebarResizing(false);
        }
      }}
      onDoubleClick={() => resetSidebarWidth()}
      onKeyDown={(event) => {
        const measured = measure(event.currentTarget);
        // Visual growth direction: ArrowRight grows an LTR sidebar and shrinks an RTL one.
        const grow = measured?.rtl ? -1 : 1;
        if (event.key === "ArrowRight") {
          setSidebarWidth(sidebarWidth + grow * KEYBOARD_STEP);
        } else if (event.key === "ArrowLeft") {
          setSidebarWidth(sidebarWidth - grow * KEYBOARD_STEP);
        } else if (event.key === "Home") {
          setSidebarWidth(SIDEBAR_MIN_WIDTH);
        } else if (event.key === "End") {
          setSidebarWidth(SIDEBAR_MAX_WIDTH);
        } else if (event.key === "Enter") {
          resetSidebarWidth();
        } else {
          return;
        }
        event.preventDefault();
      }}
    />
  );
}
